import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
    listCommunicationMessages,
    listCommunicationThreads,
    markCommunicationThreadRead,
    postCommunicationMessage,
    resolveCommunicationThread,
    editChatMessage,
    deleteChatMessage,
    renameChatThread,
} from '../services/communicationChatService';

import { useRealTimeChat } from '../context/RealTimeChatContext';
import { normalizeChatMessage, normalizeChatMessages, deduplicateMessages } from '../../../utils/normalizeChatMessage';

const parseData = (response) => response?.data || {};


const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeThreads = (data) => {
    let list = [];
    if (Array.isArray(data)) list = data;
    else if (Array.isArray(data?.threads)) list = data.threads;
    else if (Array.isArray(data?.items)) list = data.items;

    if (list.length < 2) return list;
    return [...list].sort((a, b) => {
        const dateA = a?.last_message_at || a?.updated_at || a?.created_at || 0;
        const dateB = b?.last_message_at || b?.updated_at || b?.created_at || 0;
        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();
        if (isNaN(timeA)) return 1;
        if (isNaN(timeB)) return -1;
        return timeB - timeA;
    });
};

const normalizeMessages = (data) => {
    let list = [];
    if (Array.isArray(data)) list = data;
    else if (Array.isArray(data?.messages)) list = data.messages;
    else if (Array.isArray(data?.items)) list = data.items;

    if (list.length < 2) return list;
    return [...list].sort((a, b) => {
        const dateA = a?.created_at || a?.timestamp || a?.time || 0;
        const dateB = b?.created_at || b?.timestamp || b?.time || 0;
        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();
        if (isNaN(timeA)) return 1;
        if (isNaN(timeB)) return -1;
        return timeA - timeB;
    });
};

const getErrorMessage = (error, fallback) =>
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

const getCurrentUserId = (currentUser) => {
    const parsed = currentUser || {};
    return (
        parsed.user_id ||
        parsed.id ||
        parsed._id ||
        parsed.userId ||
        parsed.employee_id ||
        ''
    );
};

export const useCommunicationChat = ({ rfpId, currentUser, isModalOpen, isTeamTabActive, skipAutoSelectFirst }) => {
    const [threads, setThreads] = useState([]);
    const [selectedThreadId, setSelectedThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingThreads, setLoadingThreads] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [error, setError] = useState('');
    const [typingUsers, setTypingUsers] = useState([]);
    const [readWatermarks, setReadWatermarks] = useState({});
    const typingTimeoutRefs = useRef({});

    useEffect(() => {
        const refs = typingTimeoutRefs.current;
        return () => {
            Object.values(refs).forEach(clearTimeout);
        };
    }, []);

    const selectedIdRef = useRef(null);
    useEffect(() => {
        selectedIdRef.current = selectedThreadId;
    }, [selectedThreadId]);

    const currentUserId = useMemo(() => getCurrentUserId(currentUser), [currentUser]);
    const hasRfpContext = Boolean(rfpId);

    const refreshThreads = useCallback(async (showLoading = true) => {
        if (!rfpId || !isTeamTabActive) return [];
        if (showLoading) setLoadingThreads(true);
        setError('');
        try {
            const response = await listCommunicationThreads(rfpId);
            const threadRows = normalizeThreads(parseData(response));
            setThreads(threadRows);
            return threadRows;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load chat threads.'));
            return [];
        } finally {
            if (showLoading) setLoadingThreads(false);
        }
    }, [rfpId, isTeamTabActive]);

    const markRead = useCallback(async (threadId, messageId) => {
        if (!rfpId || !threadId) return;
        const payload = messageId ? { message_id: messageId } : {};
        try {
            await markCommunicationThreadRead(rfpId, threadId, payload);
        } catch (_err) {
            // non-blocking read marker call
        }
    }, [rfpId]);

    const openThread = useCallback(async (threadId, showLoading = true) => {
        if (!rfpId || !threadId || !isTeamTabActive) return [];
        setSelectedThreadId(threadId);
        if (showLoading) setLoadingMessages(true);
        setError('');
        try {
            const response = await listCommunicationMessages(rfpId, threadId);
            const rawMessages = normalizeMessages(parseData(response));
            // Normalize every message to primitives — fixes "Objects are not valid as a React child"
            const messageRows = deduplicateMessages(normalizeChatMessages(rawMessages));
            setMessages(messageRows);
            const latest = messageRows[messageRows.length - 1];
            const latestId = latest?.message_id || latest?.id || latest?._id;
            await markRead(threadId, latestId);
            return messageRows;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load messages.'));
            setMessages([]);
            return [];
        } finally {
            if (showLoading) setLoadingMessages(false);
        }
    }, [markRead, rfpId, isTeamTabActive]);

    const sendMessage = useCallback(async (text, mentionedUserIds = []) => {
        const content = (text || '').trim();
        if (!rfpId || !selectedThreadId || !content) return false;
        setSendingMessage(true);
        setError('');
        try {
            const payload = { body: content };
            if (mentionedUserIds && mentionedUserIds.length > 0) {
                payload.mentioned_user_ids = mentionedUserIds;
            }
            await postCommunicationMessage(rfpId, selectedThreadId, payload);
            await openThread(selectedThreadId, false);
            await refreshThreads(false);
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to send message.'));
            return false;
        } finally {
            setSendingMessage(false);
        }
    }, [openThread, refreshThreads, rfpId, selectedThreadId]);

    const resolveThread = useCallback(async (payload) => {
        if (!rfpId) return null;
        setError('');
        try {
            const response = await resolveCommunicationThread(rfpId, payload);
            const resolved = parseData(response);
            const resolvedThreadId = resolved?.thread_id || resolved?.id || resolved?._id;
            const latestThreads = await refreshThreads();
            if (resolvedThreadId) {
                await openThread(resolvedThreadId);
                return resolvedThreadId;
            }
            const firstThread = asArray(latestThreads)[0];
            const fallbackThreadId = firstThread?.thread_id || firstThread?.id || firstThread?._id;
            if (fallbackThreadId) {
                await openThread(fallbackThreadId);
            }
            return fallbackThreadId || null;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create/open thread.'));
            return null;
        }
    }, [openThread, refreshThreads, rfpId]);

    const handleEditMessage = useCallback(async (messageId, newBody) => {
        if (!messageId || !newBody.trim()) return false;
        try {
            await editChatMessage(messageId, newBody);
            if (selectedThreadId) {
                await openThread(selectedThreadId, false);
            }
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to edit message.'));
            return false;
        }
    }, [openThread, selectedThreadId]);

    const handleDeleteMessage = useCallback(async (messageId) => {
        if (!messageId) return false;
        try {
            await deleteChatMessage(messageId);
            if (selectedThreadId) {
                await openThread(selectedThreadId, false);
            }
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete message.'));
            return false;
        }
    }, [openThread, selectedThreadId]);

    const handleRenameThread = useCallback(async (threadId, title) => {
        if (!threadId || !title.trim()) return false;
        try {
            await renameChatThread(threadId, title);
            await refreshThreads(false);
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to rename thread.'));
            return false;
        }
    }, [refreshThreads]);

    const refreshOnFocus = useCallback(async () => {
        if (!isModalOpen || !rfpId) return;
        const nextThreads = await refreshThreads();
        const selected =
            selectedIdRef.current ||
            (skipAutoSelectFirst ? null : (nextThreads[0]?.thread_id || nextThreads[0]?.id || nextThreads[0]?._id));
        if (selected) {
            await openThread(selected);
        }
    }, [isModalOpen, openThread, refreshThreads, rfpId, skipAutoSelectFirst]);

    useEffect(() => {
        if (!isModalOpen || !rfpId) return;
        refreshOnFocus();
    }, [isModalOpen, refreshOnFocus, rfpId]);

    useEffect(() => {
        const handleFocus = () => {
            refreshOnFocus();
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [refreshOnFocus]);

    useEffect(() => {
        if (!rfpId) {
            setThreads([]);
            setSelectedThreadId(null);
            setMessages([]);
            setError('');
        }
    }, [rfpId]);

    const { lastMessage, connectionStatus } = useRealTimeChat();

    // Listen for real-time messages from WebSocket
    useEffect(() => {
        if (!lastMessage) return;

        // Handle Real-time Typing Indicators
        if (lastMessage.event === 'typing') {
            const incomingThreadId = lastMessage.thread_id || lastMessage.threadId;
            if (String(incomingThreadId) === String(selectedThreadId)) {
                const typingUserId = lastMessage.user_id || lastMessage.userId;
                const isTyping = lastMessage.is_typing;

                if (typingUserId && String(typingUserId) !== String(currentUserId)) {
                    if (isTyping) {
                        setTypingUsers(prev => prev.includes(typingUserId) ? prev : [...prev, typingUserId]);

                        if (typingTimeoutRefs.current[typingUserId]) {
                            clearTimeout(typingTimeoutRefs.current[typingUserId]);
                        }

                        typingTimeoutRefs.current[typingUserId] = setTimeout(() => {
                            setTypingUsers(prev => prev.filter(id => id !== typingUserId));
                            delete typingTimeoutRefs.current[typingUserId];
                        }, 5000);
                    } else {
                        if (typingTimeoutRefs.current[typingUserId]) {
                            clearTimeout(typingTimeoutRefs.current[typingUserId]);
                            delete typingTimeoutRefs.current[typingUserId];
                        }
                        setTypingUsers(prev => prev.filter(id => id !== typingUserId));
                    }
                }
            }
            return;
        }

        const { type } = lastMessage;

        // Handle Thread Read Status Updates
        if (type === 'THREAD_READ_STATUS_UPDATED') {
            const threadId = lastMessage.thread_id || lastMessage.threadId;
            const readerId = lastMessage.user_id_who_read;
            
            if (readerId && String(readerId) !== String(currentUserId)) {
                setReadWatermarks(prev => ({
                    ...prev,
                    [threadId]: Date.now()
                }));
            }

            if (isTeamTabActive) {
                // We use a safe local call here to avoid dependency loops
                listCommunicationThreads(rfpId).then(res => {
                    const threadRows = normalizeThreads(res?.data || {});
                    setThreads(threadRows);
                }).catch(() => { });
            }
            return;
        }

        if (type !== 'CHAT_MESSAGE_RECEIVED') return;

        const incomingThreadId = lastMessage.thread_id || lastMessage.threadId || lastMessage.data?.thread_id;
        // Use _rawMessage (original object preserved by RealTimeChatContext sanitizer) if available,
        // otherwise fall back to the sanitized string in .message
        const rawIncomingMessage = lastMessage._rawMessage || lastMessage.message || lastMessage.data?.message;

        if (!incomingThreadId || !rawIncomingMessage) return;

        // Normalize before touching state — THIS is the fix for "Objects are not valid as a React child"
        const incomingMessage = normalizeChatMessage(rawIncomingMessage);

        if (String(incomingThreadId) === String(selectedThreadId)) {
            setMessages(prev => {
                // Deduplicate: skip if already present
                if (prev.some(m => String(m.id || m._id || m.message_id) === String(incomingMessage.id))) return prev;
                return [...prev, incomingMessage];
            });

            markRead(incomingThreadId, incomingMessage.id);
        }

        // Update thread list unread counts WITHOUT triggering a full messages reload
        if (isTeamTabActive) {
            listCommunicationThreads(rfpId).then(res => {
                const threadRows = normalizeThreads(res?.data || {});
                setThreads(threadRows);
            }).catch(() => { });
        }

    }, [lastMessage, selectedThreadId, markRead, rfpId, currentUserId, isTeamTabActive]); // Removed refreshThreads to prevent loops

    // Removed polling hack so BE bug can be verified.
    const unreadCount = useMemo(
        () =>
            threads.reduce((sum, thread) => {
                const unread = Number(thread?.unread_count || thread?.unread || 0);
                return Number.isFinite(unread) ? sum + unread : sum;
            }, 0),
        [threads]
    );

    return {
        hasRfpContext,
        currentUserId,
        threads,
        selectedThreadId,
        messages,
        loadingThreads,
        loadingMessages,
        sendingMessage,
        unreadCount,
        error,
        setError,
        refreshThreads,
        openThread,
        sendMessage,
        markRead,
        resolveThread,
        refreshOnFocus,
        connectionStatus,
        typingUsers,
        readWatermarks,
        editMessage: handleEditMessage,
        deleteMessage: handleDeleteMessage,
        renameThread: handleRenameThread,
    };
};

